<template>
  <BaseModal :modelValue="isOpen" @update:modelValue="onConfirmDialogUpdate" :title="title" :subtitle="message" size="sm" :showClose="false">
    <div class="p-6">
      <div class="flex items-start gap-4">
        <div class="flex-shrink-0">
          <div class="w-12 h-12 rounded-full flex items-center justify-center" :class="variant === 'danger' ? 'bg-red-100' : 'bg-blue-100'">
            <svg v-if="variant === 'danger'" class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <svg v-else class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        <div class="flex-1">
          <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ title }}</h3>
          <p class="text-sm text-gray-600">{{ message }}</p>
        </div>
      </div>
    </div>
    <template #footer>
      <button
        @click="handleCancel"
        class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
      >
        {{ cancelText }}
      </button>
      <button
        @click="handleConfirm"
        class="px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
        :class="variant === 'danger' 
          ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' 
          : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'"
      >
        {{ confirmText }}
      </button>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import BaseModal from './BaseModal.vue'

interface ConfirmOptions {
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'info'
}

const isOpen = ref(false)
const title = ref('')
const message = ref('')
const confirmText = ref('Confirmer')
const cancelText = ref('Annuler')
const variant = ref<'danger' | 'info'>('info')
let resolvePromise: ((value: boolean) => void) | null = null

const show = (options: ConfirmOptions): Promise<boolean> => {
  title.value = options.title
  message.value = options.message
  confirmText.value = options.confirmText || 'Confirmer'
  cancelText.value = options.cancelText || 'Annuler'
  variant.value = options.variant || 'info'
  isOpen.value = true
  
  return new Promise((resolve) => {
    resolvePromise = resolve
  })
}

const onConfirmDialogUpdate = (val: boolean) => {
  isOpen.value = val
  if (!val && resolvePromise) {
    resolvePromise(false)
    resolvePromise = null
  }
}

const handleConfirm = () => {
  isOpen.value = false
  if (resolvePromise) {
    resolvePromise(true)
    resolvePromise = null
  }
}

const handleCancel = () => {
  isOpen.value = false
  if (resolvePromise) {
    resolvePromise(false)
    resolvePromise = null
  }
}

defineExpose({
  show
})
</script>

<style scoped>
@keyframes scale-in {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-scale-in {
  animation: scale-in 0.2s ease-out;
}
</style>
